import React, { useState, useCallback } from "react";
import Colors from "../PageColorPalette/Colors";
import chroma from "chroma-js";
import {
  wrapper,
  wrapper_grid,
  gradient_result,
  settings,
  heading,
  button_ad,
  colors_controls,
  colors_scale,
  delete_button,
  orientation_select,
  gradient_type_selector,
  gradient_type_button,
  gradient_type_button_active,
  presets_container,
  presets_grid,
  preset_button,
  actions_bar,
  action_button,
  toast,
} from "./styles.module.css";
import Code from "../../../coloreshtml/template/components/codeSnippetColor/Code";

// Textos en múltiples idiomas
const TEXTS = {
  es: {
    mainTitle: "Generador de Degradados CSS",
    settings: "Ajustes del gradiente",
    generatedGradient: "Degradado generado",
    colorRange: "Gama de colores",
    addColor: "Agregar color",
    orientation: "Orientación",
    gradientType: "Tipo de gradiente",
    linear: "Lineal",
    radial: "Radial",
    conic: "Cónico",
    presets: "Presets populares",
    copyCSS: "Copiar CSS",
    copySVG: "Copiar SVG",
    random: "Aleatorio",
    reverse: "Invertir",
    copied: "¡Copiado!",
  },
  en: {
    mainTitle: "CSS Gradient Generator",
    settings: "Gradient Settings",
    generatedGradient: "Generated Gradient",
    colorRange: "Color Range",
    addColor: "Add Color",
    orientation: "Orientation",
    gradientType: "Gradient Type",
    linear: "Linear",
    radial: "Radial",
    conic: "Conic",
    presets: "Popular Presets",
    copyCSS: "Copy CSS",
    copySVG: "Copy SVG",
    random: "Random",
    reverse: "Reverse",
    copied: "Copied!",
  },
  pt: {
    mainTitle: "Gerador de Gradientes CSS",
    settings: "Configurações do Gradiente",
    generatedGradient: "Gradiente Gerado",
    colorRange: "Faixa de Cores",
    addColor: "Adicionar Cor",
    orientation: "Orientação",
    gradientType: "Tipo de Gradiente",
    linear: "Linear",
    radial: "Radial",
    conic: "Cônico",
    presets: "Presets Populares",
    copyCSS: "Copiar CSS",
    copySVG: "Copiar SVG",
    random: "Aleatório",
    reverse: "Inverter",
    copied: "Copiado!",
  },
  fr: {
    mainTitle: "Générateur de Dégradés CSS",
    settings: "Paramètres du dégradé",
    generatedGradient: "Dégradé généré",
    colorRange: "Gamme de couleurs",
    addColor: "Ajouter une couleur",
    orientation: "Orientation",
    gradientType: "Type de dégradé",
    linear: "Linéaire",
    radial: "Radial",
    conic: "Conique",
    presets: "Préréglages populaires",
    copyCSS: "Copier CSS",
    copySVG: "Copier SVG",
    random: "Aléatoire",
    reverse: "Inverser",
    copied: "Copié!",
  },
  de: {
    mainTitle: "CSS Farbverlauf-Generator",
    settings: "Farbverlaufs-Einstellungen",
    generatedGradient: "Generierter Farbverlauf",
    colorRange: "Farbbereich",
    addColor: "Farbe hinzufügen",
    orientation: "Ausrichtung",
    gradientType: "Verlaufsart",
    linear: "Linear",
    radial: "Radial",
    conic: "Kegelförmig",
    presets: "Beliebte Presets",
    copyCSS: "CSS kopieren",
    copySVG: "SVG kopieren",
    random: "Zufällig",
    reverse: "Umkehren",
    copied: "Kopiert!",
  },
};

// Presets de gradientes populares
const GRADIENT_PRESETS = [
  [
    { color: "#667eea", stop: 0 },
    { color: "#764ba2", stop: 100 },
  ],
  [
    { color: "#f093fb", stop: 0 },
    { color: "#f5576c", stop: 100 },
  ],
  [
    { color: "#4facfe", stop: 0 },
    { color: "#00f2fe", stop: 100 },
  ],
  [
    { color: "#43e97b", stop: 0 },
    { color: "#38f9d7", stop: 100 },
  ],
  [
    { color: "#fa709a", stop: 0 },
    { color: "#fee140", stop: 100 },
  ],
  [
    { color: "#a8edea", stop: 0 },
    { color: "#fed6e3", stop: 100 },
  ],
  [
    { color: "#ff0844", stop: 0 },
    { color: "#ffb199", stop: 100 },
  ],
  [
    { color: "#30cfd0", stop: 0 },
    { color: "#330867", stop: 100 },
  ],
];

const ORIENTATIONS = {
  linear: [
    { value: "to right", label: "→ Right" },
    { value: "to left", label: "← Left" },
    { value: "to top", label: "↑ Top" },
    { value: "to bottom", label: "↓ Bottom" },
    { value: "to top right", label: "↗ Top Right" },
    { value: "to top left", label: "↖ Top Left" },
    { value: "to bottom right", label: "↘ Bottom Right" },
    { value: "to bottom left", label: "↙ Bottom Left" },
    { value: "45deg", label: "45°" },
    { value: "90deg", label: "90°" },
    { value: "135deg", label: "135°" },
    { value: "180deg", label: "180°" },
  ],
  radial: [
    { value: "circle", label: "Circle" },
    { value: "ellipse", label: "Ellipse" },
    { value: "circle at center", label: "Circle Center" },
    { value: "circle at top", label: "Circle Top" },
    { value: "circle at bottom", label: "Circle Bottom" },
    { value: "ellipse at center", label: "Ellipse Center" },
  ],
  conic: [
    { value: "from 0deg", label: "From 0°" },
    { value: "from 90deg", label: "From 90°" },
    { value: "from 180deg", label: "From 180°" },
    { value: "from 0deg at center", label: "Center 0°" },
    { value: "from 0deg at top left", label: "Top Left" },
  ],
};

const GradientGenerator = ({ locale }) => {
  const texts = TEXTS[locale] || TEXTS.en;

  const [gradientType, setGradientType] = useState("linear");
  const [colorStops, setColorStops] = useState([
    { color: "#667eea", stop: 0 },
    { color: "#764ba2", stop: 100 },
  ]);
  const [orientation, setOrientation] = useState("to right");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const colorScale = colorStops.map(c => c.color);

  // Mostrar toast
  const showToastMessage = useCallback(message => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  // Generar CSS del gradiente
  const generateGradientCSS = useCallback(() => {
    const stops = colorStops.map(cs => `${cs.color} ${cs.stop}%`).join(", ");

    switch (gradientType) {
      case "radial":
        return `radial-gradient(${orientation}, ${stops})`;
      case "conic":
        return `conic-gradient(${orientation}, ${stops})`;
      default:
        return `linear-gradient(${orientation}, ${stops})`;
    }
  }, [colorStops, gradientType, orientation]);

  const gradientCSS = generateGradientCSS();

  const gradientStyle = {
    backgroundImage: gradientCSS,
    width: "100%",
    height: "100%",
  };

  // Handlers
  const handleColorChange = (index, e) => {
    const newStops = [...colorStops];
    newStops[index].color = e.target.value;
    setColorStops(newStops);
  };

  const handleStopChange = (index, e) => {
    const newStops = [...colorStops];
    newStops[index].stop = parseInt(e.target.value);
    setColorStops(newStops);
  };

  const addColorStop = () => {
    const newColor = chroma.random().hex();
    const newStop = Math.round(
      colorStops.reduce((acc, cs) => acc + cs.stop, 0) / colorStops.length
    );
    setColorStops([...colorStops, { color: newColor, stop: newStop }]);
  };

  const removeColorStop = index => {
    if (colorStops.length > 2) {
      setColorStops(colorStops.filter((_, i) => i !== index));
    }
  };

  const applyPreset = preset => {
    setColorStops([...preset]);
  };

  const generateRandom = () => {
    const numColors = Math.floor(Math.random() * 3) + 2;
    const newStops = Array.from({ length: numColors }, (_, i) => ({
      color: chroma.random().hex(),
      stop: Math.round((i / (numColors - 1)) * 100),
    }));
    setColorStops(newStops);
  };

  const reverseColors = () => {
    setColorStops(
      colorStops
        .map((cs, i, arr) => ({
          ...cs,
          stop: arr[arr.length - 1 - i].stop,
        }))
        .reverse()
    );
  };

  const copyCSS = () => {
    const css = `.element {\n  background-image: ${gradientCSS};\n}`;
    navigator.clipboard.writeText(css);
    showToastMessage(texts.copied);
  };

  const handleGradientTypeChange = type => {
    setGradientType(type);
    setOrientation(ORIENTATIONS[type][0].value);
  };

  return (
    <section className={wrapper}>
      <h1>{texts.mainTitle}</h1>

      <div className={wrapper_grid}>
        {/* Panel de configuración */}
        <div className={settings}>
          <h2 className={heading}>{texts.settings}</h2>

          {/* Selector de tipo de gradiente */}
          <div className={gradient_type_selector}>
            {["linear", "radial", "conic"].map(type => (
              <button
                key={type}
                className={`${gradient_type_button} ${
                  gradientType === type ? gradient_type_button_active : ""
                }`}
                onClick={() => handleGradientTypeChange(type)}
              >
                {texts[type]}
              </button>
            ))}
          </div>

          {/* Selector de orientación */}
          <select
            className={orientation_select}
            value={orientation}
            onChange={e => setOrientation(e.target.value)}
          >
            {ORIENTATIONS[gradientType].map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Controles de colores */}
          {colorStops.map((colorStop, index) => (
            <div className={colors_controls} key={index}>
              <input
                type="color"
                value={colorStop.color}
                onChange={e => handleColorChange(index, e)}
              />
              <input
                type="range"
                min={0}
                max={100}
                value={colorStop.stop}
                onChange={e => handleStopChange(index, e)}
              />
              <button
                className={delete_button}
                onClick={() => removeColorStop(index)}
                disabled={colorStops.length <= 2}
                style={{ opacity: colorStops.length <= 2 ? 0.5 : 1 }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          ))}

          {/* Botón agregar color */}
          <button className={button_ad} onClick={addColorStop}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            {texts.addColor}
          </button>

          {/* Acciones */}
          <div className={actions_bar}>
            <button className={action_button} onClick={generateRandom}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {texts.random}
            </button>
            <button className={action_button} onClick={reverseColors}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              {texts.reverse}
            </button>
            <button className={action_button} onClick={copyCSS}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              {texts.copyCSS}
            </button>
          </div>

          {/* Presets */}
          <div className={presets_container}>
            <h3 className={heading}>{texts.presets}</h3>
            <div className={presets_grid}>
              {GRADIENT_PRESETS.map((preset, index) => (
                <button
                  key={index}
                  className={preset_button}
                  style={{
                    background: `linear-gradient(135deg, ${preset[0].color}, ${preset[1].color})`,
                  }}
                  onClick={() => applyPreset(preset)}
                  title={`${preset[0].color} → ${preset[1].color}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Vista previa del gradiente */}
        <div>
          <h2 className={heading}>{texts.generatedGradient}</h2>
          <div className={gradient_result} style={gradientStyle} />
        </div>

        {/* Escala de colores */}
        <div className={colors_scale}>
          <h2 className={heading}>{texts.colorRange}</h2>
          <Colors
            palette={chroma.scale(colorScale).mode("lch").colors(7)}
            withLinkColor={false}
          />
        </div>
      </div>

      {/* Código CSS */}
      <Code
        snippet={`.element {\n  background-image: ${gradientCSS};\n}`}
        language="css"
      />

      {/* Toast de notificación */}
      {showToast && <div className={toast}>{toastMessage}</div>}
    </section>
  );
};

export default GradientGenerator;
